import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, Input, Message } from '@fx-ui/fine-design';
import { SearchList } from '../../components';
import {
    API,
    find,
    get,
    getSwitchCorpRedirectUri,
    getUrlParameter,
    insertURLQuery,
    isEmpty,
    parseInviteLink,
    redirect,
    size,
    trim,
    URL_PARAMETER
} from '@libs/shared';
import { useAuthMfa } from '../auth-mfa';
import { createCorp, fetchCorpInfo, joinCorpVerify } from '../../apis';
import { platformIntl, SITE_ROUTER, TRACKER, UGC_MAX_LENGTH } from '../../constants';
import { getSiteUrl, getRequestUrl, hasFunction, tracker } from '../../utils';

interface ICorpJoinCreateDialogProps {
    type: 'join' | 'create';
    visible: boolean;
    setVisible: (v: boolean) => void;
    onBeforeCorpAction?: (type: 'join' | 'create') => void | Promise<{ redirectUri?: string }>;
}

const { CORP_NAME } = UGC_MAX_LENGTH.USER;

/**
 * 空状态企业账号（没有加入任何企业且没有创建自己的企业）进入工作台后可以创建企业或者加入企业
 * 1、创建企业/团队弹窗
 * 2、加入企业/团队弹窗
 * @param props ICorpJoinCreateDialogProps
 */
const CorpJoinCreateDialog: React.FunctionComponent<ICorpJoinCreateDialogProps> = (props) => {
    const { type, visible, setVisible, onBeforeCorpAction } = props;
    const [corpName, setCorpName] = useState<string>('');
    const [inviteLink, setInviteLink] = useState<string>('');
    const [qiXinCorpList, setQiXinCorpList] = useState<any[]>();
    const [companyDetail, setCompanyDetail] = useState<any>();
    const searchInput = useRef<any>();
    const { handleMfaError, renderAuthMfa } = useAuthMfa();

    useEffect(() => {
        if (!visible) {
            setInviteLink('');
            setCorpName('');
        }
    }, [visible]);

    /**
     * 确认加入企业
     *
     * @param joinCorpId 企业id
     * @param deptNo 部门编号
     * @param invitationUrl 邀请链接
     */
    const joinCorp = (joinCorpId: string, deptNo?: string, invitationUrl?: string) => {
        const data: Record<string, unknown> = {
            is_public: true,
            invited_by: joinCorpId
        };
        if (deptNo) {
            data.dept_no = deptNo;
        }
        // 加入企业
        return joinCorpVerify(data, invitationUrl)
            .then((res) => {
                tracker(TRACKER.USAGE_MODE, {
                    method: 'join',
                    corpName: get(res, 'data.corp_name', '')
                });
                Message.add({
                    messageType: 'toast',
                    message: platformIntl.get('corp.join.success'),
                    type: 'success',
                    onClose: () => {
                        Promise.resolve(onBeforeCorpAction?.('join'))
                            .then((result) => {
                                redirect(
                                    insertURLQuery(getSiteUrl(SITE_ROUTER.ACCOUNT.SWITCH_CORP), [
                                        {
                                            key: URL_PARAMETER.REDIRECT_URI,
                                            value: getSwitchCorpRedirectUri(result?.redirectUri)
                                        },
                                        {
                                            key: URL_PARAMETER.TOKEN,
                                            value: res.data.token
                                        },
                                        {
                                            key: URL_PARAMETER.TARGET,
                                            value: res.data.tenant_id
                                        }
                                    ])
                                );
                            })
                            .catch(() => undefined);
                    }
                });
            })
            .catch((error) => {
                handleMfaError(error, () => {
                    joinCorp(joinCorpId, deptNo, invitationUrl);
                });
                return Promise.reject();
            });
    };

    const handleCorpJoin = () => {
        const { corpId: joinCorpId } = parseInviteLink(inviteLink);
        const deptNo = getUrlParameter('dept_no', inviteLink);
        if (!isEmpty(joinCorpId)) {
            // 查询企业信息
            return fetchCorpInfo(joinCorpId, inviteLink)
                .then((res) => {
                    const data = get(res, 'data');
                    if (!isEmpty(get(data, 'name')))
                        Alert.openAlert({
                            title: platformIntl.get('corp.join.confirm', {
                                name: get(data, 'name')
                            }),
                            type: 'query',
                            footerLeft: (
                                <Button type="text" onClick={() => Alert.closeAlert()}>
                                    {platformIntl.get('member.invite.link.change')}
                                </Button>
                            ),
                            onOk: () => {
                                return joinCorp(joinCorpId, deptNo, inviteLink);
                            }
                        });
                })
                .catch(() => {
                    Message.add({
                        messageType: 'toast',
                        message: platformIntl.get('corp.join.url.error'),
                        type: 'warning'
                    });
                });
        }
    };

    if (type === 'join') {
        return (
            <>
                <Dialog
                    className="fx-corp-join-create-dialog"
                    title={platformIntl.get('corp.join')}
                    visible={visible}
                    height={240}
                    width={500}
                    text4Ok={null}
                    text4Cancel={null}
                    onCancel={() => {
                        setVisible(false);
                    }}
                >
                    <div className="input-title">
                        {platformIntl.get('member.invite.link')}
                        <span>*</span>
                    </div>
                    <Input
                        placeholder={platformIntl.get('contact.connection.join.input')}
                        className="join-create-dialog-input"
                        value={inviteLink}
                        onChange={(v) => {
                            setInviteLink(v);
                        }}
                    />
                    <Button
                        disabled={isEmpty(inviteLink)}
                        className="join-create-dialog-btn"
                        type="primary"
                        onClick={() => {
                            if (parseInviteLink(inviteLink).isMatched) {
                                handleCorpJoin();
                            } else {
                                Message.add({
                                    messageType: 'toast',
                                    type: 'warning',
                                    message: platformIntl.get('contact.connection.join.fail.tip')
                                });
                            }
                        }}
                    >
                        {platformIntl.get('join')}
                    </Button>
                </Dialog>
                {renderAuthMfa()}
            </>
        );
    } else {
        return (
            <Dialog
                className="fx-corp-join-create-dialog"
                title={platformIntl.get('corp.create')}
                visible={visible}
                height={240}
                width={500}
                text4Ok={null}
                text4Cancel={null}
                onCancel={() => {
                    setVisible(false);
                }}
            >
                <div onClick={() => searchInput.current && searchInput.current.closePopover()}>
                    <div className="input-title">
                        {platformIntl.get('corp.team.name')}
                        <span>*</span>
                    </div>
                    <SearchList
                        errMsg={platformIntl.get('max.length.error.message', { max: CORP_NAME })}
                        maxLength={CORP_NAME}
                        placeholder={platformIntl.get('contact.connection.create.input')}
                        className="join-create-dialog-input"
                        showSearchResult={hasFunction('qixin_search_service')}
                        value={corpName}
                        items={qiXinCorpList}
                        ref={searchInput}
                        async={{
                            // 启信宝企业信息查询
                            url: getRequestUrl(API.conf.search_corp),
                            data: {
                                matchType: [
                                    'ename',
                                    'product',
                                    'copyright',
                                    'software',
                                    'trademark'
                                ]
                            }
                        }}
                        onAsyncSuccess={(res) => {
                            const corps = get(res, 'data.corps', []);
                            setQiXinCorpList(corps);
                        }}
                        onItemClick={(item) => {
                            setCorpName(get(item, 'name', ''));
                            setCompanyDetail(item);
                        }}
                        onReturn={(item) => {
                            setCorpName(get(item, 'name', ''));
                        }}
                        onInputChange={(v) => {
                            setCorpName(v);
                        }}
                        onPopupVisibleChange={(visible) => {
                            // 失焦时若有企业名称与keyword一致且当前无选中企业,选择该企业
                            if (!visible) {
                                const exactlySameItem = find(qiXinCorpList, (v) => {
                                    return v.name === corpName;
                                });
                                if (exactlySameItem && !companyDetail) {
                                    setCorpName(get(exactlySameItem, 'name', ''));
                                    setCompanyDetail(exactlySameItem);
                                }
                            }
                        }}
                    />
                    <Button
                        disabled={isEmpty(trim(corpName))}
                        className="join-create-dialog-btn"
                        type="primary"
                        loadingWhenClick
                        onClick={() => {
                            const trimName = trim(corpName);
                            if (size(trimName) > CORP_NAME) {
                                return;
                            }
                            // 点击【创建】后，让该按钮一直处于loading状态，防止重复点击，和 FXD-41168 同理
                            return new Promise(() => {
                                createCorp({
                                    name: trimName,
                                    persona: {
                                        company_detail: companyDetail
                                    }
                                }).then((res) => {
                                    Message.add({
                                        messageType: 'toast',
                                        message: platformIntl.get('corp.create.success'),
                                        type: 'success',
                                        onClose: () => {
                                            Promise.resolve(onBeforeCorpAction?.('create'))
                                                .then((result) => {
                                                    redirect(
                                                        insertURLQuery(
                                                            getSiteUrl(
                                                                SITE_ROUTER.ACCOUNT.SWITCH_CORP
                                                            ),
                                                            [
                                                                {
                                                                    key: URL_PARAMETER.REDIRECT_URI,
                                                                    value: getSwitchCorpRedirectUri(
                                                                        result?.redirectUri
                                                                    )
                                                                },
                                                                {
                                                                    key: URL_PARAMETER.TOKEN,
                                                                    value: res.data.token
                                                                },
                                                                {
                                                                    key: URL_PARAMETER.TARGET,
                                                                    value: res.data.tenant?.id
                                                                }
                                                            ]
                                                        )
                                                    );
                                                })
                                                .catch(() => undefined);
                                        }
                                    });
                                });
                            });
                        }}
                    >
                        {platformIntl.get('creation')}
                    </Button>
                </div>
            </Dialog>
        );
    }
};

export default CorpJoinCreateDialog;
